import type {
  LeaveRequest,
  ReportRow,
  ScheduleResponse,
  Student,
  TrendPoint,
} from "./types";

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = localStorage.getItem("jishi_token") ?? "";
  return {
    accept: "application/json",
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

function handle401(): never {
  localStorage.removeItem("jishi_token");
  window.location.reload();
  throw new Error("Unauthorized");
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: authHeaders() });
  if (res.status === 401) handle401();
  if (!res.ok) throw new Error(`${res.status} ${path}: ${await res.text()}`);
  return (await res.json()) as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (res.status === 401) handle401();
  if (!res.ok) throw new Error(`${res.status} ${path}: ${await res.text()}`);
  return (await res.json()) as T;
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PUT",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (res.status === 401) handle401();
  if (!res.ok) throw new Error(`${res.status} ${path}: ${await res.text()}`);
  return (await res.json()) as T;
}

async function del(path: string): Promise<void> {
  const res = await fetch(path, { method: "DELETE", headers: authHeaders() });
  if (res.status === 401) handle401();
  if (!res.ok) throw new Error(`${res.status} ${path}: ${await res.text()}`);
}

export const api = {
  login: async (password: string): Promise<{ token: string }> => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) throw new Error("invalid");
    return res.json() as Promise<{ token: string }>;
  },
  students: () => get<Student[]>("/api/students"),
  createStudent: (s: Partial<Student>) => post<Student>("/api/students", s),
  updateStudent: (id: number, s: Partial<Student>) => put<Student>(`/api/students/${id}`, s),
  deleteStudent: (id: number) => del(`/api/students/${id}`),
  schedule: () => get<ScheduleResponse>("/api/schedule"),
  createSchedule: (e: { student_id: number; day: number; slot: string; end_time: string; hours: number; subject: string; date?: string; student_ids?: number[]; repeat_config?: object; series_id?: string }) =>
    post<{ id: number }>("/api/schedule", e),
  deleteSchedule: (id: number) => del(`/api/schedule/${id}`),
  deleteSeriesFuture: (seriesId: string, fromDate: string) =>
    del(`/api/schedule/series/${encodeURIComponent(seriesId)}?from=${fromDate}`),
  trend: () => get<TrendPoint[]>("/api/trend"),
  report: (period?: string) => get<ReportRow[]>(`/api/reports${period ? `?period=${period}` : ""}`),
  leaves: () => get<LeaveRequest[]>("/api/leaves"),
  createLeave: (l: { student_id: number; date: string; slot: string; reason: string }) =>
    post<LeaveRequest>("/api/leaves", l),
};
